<script setup lang="ts" generic="T">
    // @ts-nocheck
    import { computed, toRefs } from "vue";
    import moment from "moment";
    import Button from "@/components/Button.vue";
    import IconList from "@/components/calendars/overlayPanels/IconList/IconList.vue";
    import IconListItem from "@/components/calendars/overlayPanels/IconList/IconListItem.vue";
    import { EventImpl } from "@fullcalendar/core/internal";
    import TimelineEvent from "@/components/calendars/events/Timeline/TimelineEvent.vue";
    import LocationIcon from "@/assets/actionIcons/location_pin.svg";
    import PatientIcon from "@/assets/actionIcons/add_patient.svg";
    import OperationIcon from "@/assets/actionIcons/opstart_i_opjournal.svg";
    import SidaIcon from "@/assets/icons/opsida.svg";
    import OperatorIcon from "@/assets/actionIcons/add_operator.svg";
    import IntidIcon from "@/assets/installelsetid_grey.svg";
    import MissingIcon from "@/assets/installelsetid_red.svg";
    import InfoIcon from "@/assets/info_grey.svg";
    import StatusBestalldIcon from "@/assets/status_bestalld.svg";
    import StatusBokadIcon from "@/assets/statusbokad.svg";
    import StatusInskrivenIcon from "@/assets/status_inskriven.svg";
    import StatusUtskrivenIcon from "@/assets/status_utskriven.svg";
    import StatusStrukenIcon from "@/assets/status_struken.svg";
    import { Slutenvardsbesok, VardplatsStatus, VardplatsAction, SidaLabels } from "@/tabs/Vardplatsbokning/types";
    import { useStore } from "@/store";

    const store = useStore();

    const emit = defineEmits(["panelAction"]);

    const props = withDefaults(
        defineProps<{
            event: EventImpl;
            colorKey?: string;
            // OBS: Anhörigbesök blir undefined pga finns inte i BaseCalendars Map data.
            // Vi behöver inte data från anhörigbesöken just nu utan det räcker med innehållet i event, men det skulle
            // kunna behövas i framtiden om det krävs mer avancerad hantering av anhörigbesök.
            data?: Slutenvardsbesok;
            /*
                Objekt med key som är namn på möjligt namn som kan användas som color key och Map som innehåller
                key id som ska kollas mot och value index som ska användas vid hämtning av färg
                T.ex: { vardgivare: new Map([["abcdef", 1], ["glqer", 7]]) }
            */
            colorKeyIndices?: { [key: string]: Map<string, number> };
        }>(),
        {
            colorKey: "",
        }
    );

    const { event, data } = toRefs(props);

    const statusIcon = computed(() => {
        const status = data?.value?.status;

        switch (status) {
            case VardplatsStatus.BESTALLD:
                return StatusBestalldIcon;
            case VardplatsStatus.BOKAD:
                return StatusBokadIcon;
            case VardplatsStatus.INSKRIVEN:
                return StatusInskrivenIcon;
            case VardplatsStatus.UTSKRIVEN:
                return StatusUtskrivenIcon;
            case VardplatsStatus.STRUKEN:
                return StatusStrukenIcon;
            default:
                return null;
        }
    });

    const statusText = computed(() => {
        return data?.value?.status;
    });

    const panelAction = (action: string, data: Slutenvardsbesok, panelRef: { toggle: Function } = null) => {
        if (panelRef) {
            panelRef.toggle();
        }
        emit("panelAction", { action, data });
    };

    const onPanelShow = async () => {
        await store.dispatch("slutenvardsbesok/getSlutenvardsbesokById", {
            id: data?.value?.id,
            addAsUpdate: true,
            config: {
                tabbnamn: "Detaljinfo",
            },
        });
    };
</script>

<template>
    <TimelineEvent
        class="vardplats-timeline-event"
        v-bind="{ ...$attrs, ...$props, ...$slots }"
        @panelShow="onPanelShow"
    >
        <template #inner>
            <template v-if="data && !event.extendedProps.isAnhorigbesok">
                <div>
                    <span>{{ data.patient.personId_formatted }} {{ data.patient.namn_reverse }}</span>
                </div>
                <div>
                    <span>
                        {{ data.vardplatsbestallning.operator.alias }}, {{ data.vardplatsbestallning.vardgivare.alias }}
                    </span>
                </div>
                <div class="operation">
                    <span>{{ data.vardplatsbestallning.huvudoperationskod.displayName }}</span>
                </div>
            </template>

            <template v-else>
                <div>
                    <span>{{ event.extendedProps.namn }}</span>
                </div>
                <div>
                    <span>
                        Anhörig till {{ event.extendedProps.patient_personId_formatted }}
                        {{ event.extendedProps.patient_namn_reverse }}
                    </span>
                </div>
            </template>
            <span v-if="data && !data.intid && !event.extendedProps.isAnhorigbesok" class="resource-warning">
                <img src="@/assets/warning-red.svg" />
            </span>
        </template>
        <template v-if="data && !event.extendedProps.isAnhorigbesok" #panel-content>
            <IconList>
                <IconListItem :icon="LocationIcon" key="vardplats">
                    <span>{{ data.sang?.namn ?? "Vårdplats ej bestämd" }}</span>
                </IconListItem>
                <IconListItem :icon="PatientIcon" key="patient">
                    <span>{{ data.patient.personId_formatted }} {{ data.patient.namn_reverse }}</span>
                </IconListItem>
                <IconListItem :icon="OperationIcon" key="typoperation">
                    <span>{{
                        data.vardplatsbestallning.typoperation
                            ? data.vardplatsbestallning.typoperation
                            : data.vardplatsbestallning.huvudoperationskod.displayName
                    }}</span>
                </IconListItem>
                <IconListItem v-if="data.vardplatsbestallning.sida" :icon="SidaIcon" key="sida">
                    <span>{{ SidaLabels[data.vardplatsbestallning.sida] }}</span>
                </IconListItem>
                <IconListItem :icon="OperatorIcon" key="operator">
                    <span>
                        {{ data.vardplatsbestallning.operator.namn }} {{ data.vardplatsbestallning.vardgivare.namn }}
                    </span>
                </IconListItem>
                <template>
                    <IconListItem v-if="data.intid" :icon="IntidIcon" key="intid">
                        <span>
                            {{ `Inställelsetid kl ${moment(`${data.indatum}T${data.intid}`).format("HH:mm")} ` }}
                            {{ moment(data.indatum).format("YYYY-MM-DD") }}
                        </span>
                    </IconListItem>
                    <IconListItem v-else :icon="MissingIcon" key="intid">
                        <span><strong>Varning:</strong> Inställelsetid saknas</span>
                    </IconListItem>
                </template>
                <IconListItem
                    v-if="data.vardplatsbestallning.info_till_vardavdelningen"
                    :icon="InfoIcon"
                    key="info"
                    truncate
                >
                    <span>{{ data.vardplatsbestallning.info_till_vardavdelningen }}</span>
                </IconListItem>
            </IconList>
        </template>
        <template #panel-actions="{ panelRef }">
            <template v-if="data && !event.extendedProps.isAnhorigbesok">
                <div class="status-action-area">
                    <span class="status">
                        <img :src="statusIcon" />
                        <span>{{ statusText }}</span>
                    </span>
                    <Button
                        v-if="data.status === VardplatsStatus.BOKAD"
                        variant="contour"
                        size="small"
                        @click="panelAction(VardplatsAction.SKRIV_IN_PATIENT, data)"
                    >
                        <img class="img-left" src="@/assets/opstart_i_opjournal_blue.svg" />
                        <span>Skriv in patient</span>
                    </Button>
                    <Button
                        v-else-if="data.status === VardplatsStatus.INSKRIVEN"
                        variant="contour"
                        size="small"
                        @click="panelAction(VardplatsAction.SKRIV_UT_PATIENT, data)"
                    >
                        <img class="img-left" src="@/assets/opstart_i_opjournal_blue.svg" />
                        <span>Skriv ut patient</span>
                    </Button>
                </div>
                <div class="action-area">
                    <Button
                        variant="contour"
                        size="small"
                        @click="panelAction(VardplatsAction.OPEN_VARDPLATSBESTALLNING, data, panelRef)"
                    >
                        <span>Vårdplatsbeställning</span>
                        <img class="img-right" src="@/assets/arrow_right_blue.svg" />
                    </Button>
                    <Button
                        variant="contour"
                        size="small"
                        @click="panelAction(VardplatsAction.HANTERA_VARDPLATS, data)"
                    >
                        <img class="img-left" src="@/assets/opstart_i_opjournal_blue.svg" />
                        <span>Hantera vårdplats</span>
                    </Button>
                    <Button
                        v-if="![VardplatsStatus.INSKRIVEN, VardplatsStatus.UTSKRIVEN].includes(data.status)"
                        variant="contour-alert"
                        size="small"
                        @click="panelAction(VardplatsAction.STRYK_VARDPLATS, data)"
                    >
                        <img class="img-left" src="@/assets/opstart_i_opjournal_blue.svg" />
                        <span>Stryk vårdplats</span>
                    </Button>
                </div>
            </template>
            <template v-else>
                <IconList>
                    <IconListItem>
                        <span>{{ event.extendedProps.namn }}</span>
                    </IconListItem>
                    <IconListItem>
                        <span>
                            Anhörig till {{ event.extendedProps.patient_personId_formatted }}
                            {{ event.extendedProps.patient_namn_reverse }}
                        </span>
                    </IconListItem>
                </IconList>
            </template>
        </template>
    </TimelineEvent>
</template>

<style scoped lang="scss">
    @import "@/style/variables";

    .resource-warning {
        position: absolute;
        bottom: 50%;
        right: 0;
        z-index: 1000;

        img {
            width: 10px;
            height: 10px;
        }
    }

    .status-action-area {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: 5px;
        padding-bottom: 16px;
        border-bottom: 1px solid $panel-border-color;

        .status {
            font-weight: 700;
            display: flex;
            align-items: center;
            gap: 16px;
        }
    }

    .action-area {
        margin-top: 22px;
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        row-gap: 12px;

        button {
            width: 170px;
        }
    }
</style>
